import type {
  CallSessionId,
  CreateCallRuntimeTokenRequest,
  CreateCallRuntimeTokenResponse,
  IdentityId,
  PublicKey
} from '@shared/types';
import { apiClient, APIError } from '../api/client';
import { signRequestWithKey } from '../crypto/signing';
import type { SigningKey } from '../crypto/ed25519Provider';
import { useAuthStore } from '../store/authStore';
import { t } from '../i18n';
import { markCircleServerAccessRemoved } from '../utils/removedCircleAccess';
import {
  answerIncomingNativeVideoCall,
  endNativeCall,
  setNativeCallMuted,
  setNativeCallLocalVideoEnabled,
  switchNativeCallCamera,
  stopIncomingNativeRinging,
  startOutgoingNativeVideoCall
} from '../utils/webviewBridge';

type NativeCallEvent =
  | {
      type: 'state';
      callSessionId: CallSessionId;
      state: string;
      muted?: boolean;
      runtimeMode?: string;
      localVideoEnabled?: boolean;
      remoteVideoAvailable?: boolean;
      hadConnected?: boolean;
      connectedAtMs?: number;
    }
  | {
      type: 'finalized';
      callSessionId: CallSessionId;
      direction: 'incoming' | 'outgoing';
      finalStatus: 'answered' | 'ended' | 'failed' | 'rejected';
      durationSeconds?: number;
      timestampMs?: number;
      connectedAtMs?: number;
      localIdentityId?: string;
      remoteIdentityId: string;
      remoteDisplayName?: string;
      runtimeMode?: string;
      reason?: string;
    }
  | {
      type: 'ended';
      callSessionId: CallSessionId;
      reason: string;
    }
  | {
      type: 'upgrade-to-video';
      callSessionId: CallSessionId;
    }
  | {
      type: 'security-verified';
      callSessionId: CallSessionId;
      verified: boolean;
    };

const NATIVE_CALL_EVENT = 'videocall:nativeCallEvent';

type ExplicitDeviceContext = {
  deviceId?: string;
  identityId?: string;
  deviceSigningKey?: SigningKey;
};

function getCurrentDeviceContext(explicit?: ExplicitDeviceContext) {
  const auth = useAuthStore.getState();
  const resolvedDeviceId = (explicit?.deviceId || '').trim();
  if (!resolvedDeviceId) {
    throw new Error('Local call device is required');
  }
  const device = auth.devices[resolvedDeviceId];
  if (!device) {
    throw new Error('Local call device is unavailable');
  }
  const identityId = explicit?.identityId || device.identityId;
  const identity = auth.identities[identityId];
  if (!identity) {
    throw new Error('Local call identity is unavailable');
  }
  return {
    device: {
      ...device,
      signingKey: explicit?.deviceSigningKey || device.signingKey
    },
    identity
  };
}

async function createCallRuntimeToken(
  request: CreateCallRuntimeTokenRequest,
  serverUrl?: string,
  explicit?: ExplicitDeviceContext
): Promise<CreateCallRuntimeTokenResponse> {
  const { device, identity } = getCurrentDeviceContext(explicit);
  const signed = await signRequestWithKey('mobile:calls:runtime-token:create', request, device.deviceId, device.signingKey);
  let response;
  try {
    response = await apiClient.signedRequest<CreateCallRuntimeTokenResponse, CreateCallRuntimeTokenRequest>(
      '/mobile/calls/runtime-token',
      signed,
      serverUrl || identity.serverUrl
    );
  } catch (error) {
    const apiError = error instanceof APIError
      ? (error.responseJson as { error?: { code?: string; message?: string } } | null)?.error
      : undefined;
    if (
      apiError?.code === 'UNAUTHORIZED'
      && apiError.message === 'Device not found'
    ) {
      if (markCircleServerAccessRemoved(identity.identityId)) {
        window.dispatchEvent(new CustomEvent('circlus:server-access-removed', {
          detail: { identityId: identity.identityId }
        }));
      }
      throw new Error(t('servers.serverAccessRemovedAction'));
    }
    throw error;
  }
  if (response.status !== 'ok' || !response.result) {
    throw new Error(response.error?.message || 'Failed to create call runtime token');
  }
  return response.result;
}

export async function startNativeOutgoingVideoCall(params: {
  callSessionId: CallSessionId;
  remoteIdentityId: IdentityId;
  remoteDisplayName?: string;
  remoteIdentityPublicKey?: PublicKey;
  localVideoEnabled?: boolean;
  serverUrl?: string;
  localDeviceId?: string;
  localIdentityId?: string;
  deviceSigningKey?: SigningKey;
}) {
  const { identity } = getCurrentDeviceContext({
    deviceId: params.localDeviceId,
    identityId: params.localIdentityId,
    deviceSigningKey: params.deviceSigningKey
  });
  const runtimeToken = await createCallRuntimeToken({
    callSessionId: params.callSessionId,
    remoteIdentityId: params.remoteIdentityId,
    role: 'caller',
    mode: 'video-native'
  }, params.serverUrl, {
    deviceId: params.localDeviceId,
    identityId: params.localIdentityId,
    deviceSigningKey: params.deviceSigningKey
  });

  startOutgoingNativeVideoCall({
    callSessionId: params.callSessionId,
    remoteIdentityId: params.remoteIdentityId,
    remoteDisplayName: params.remoteDisplayName,
    remoteIdentityPublicKey: params.remoteIdentityPublicKey,
    localIdentityId: identity.identityId,
    localVideoEnabled: params.localVideoEnabled,
    runtimeToken
  });
}

export async function answerNativeIncomingVideoCall(params: {
  callSessionId: CallSessionId;
  remoteIdentityId: IdentityId;
  remoteDisplayName?: string;
  remoteIdentityPublicKey?: PublicKey;
  localVideoEnabled?: boolean;
  serverUrl?: string;
  localDeviceId?: string;
  localIdentityId?: string;
  deviceSigningKey?: SigningKey;
  offer: RTCSessionDescriptionInit & { dtlsSignature?: string };
}) {
  const { identity } = getCurrentDeviceContext({
    deviceId: params.localDeviceId,
    identityId: params.localIdentityId,
    deviceSigningKey: params.deviceSigningKey
  });
  const runtimeToken = await createCallRuntimeToken({
    callSessionId: params.callSessionId,
    remoteIdentityId: params.remoteIdentityId,
    role: 'callee',
    mode: 'video-native'
  }, params.serverUrl, {
    deviceId: params.localDeviceId,
    identityId: params.localIdentityId,
    deviceSigningKey: params.deviceSigningKey
  });

  answerIncomingNativeVideoCall({
    callSessionId: params.callSessionId,
    remoteIdentityId: params.remoteIdentityId,
    remoteDisplayName: params.remoteDisplayName,
    remoteIdentityPublicKey: params.remoteIdentityPublicKey,
    localIdentityId: identity.identityId,
    localVideoEnabled: params.localVideoEnabled,
    runtimeToken,
    incomingOffer: params.offer
  });
}

export function subscribeToNativeCallEvents(listener: (event: NativeCallEvent) => void): () => void {
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<NativeCallEvent>).detail;
    if (!detail) return;
    listener(detail);
  };
  window.addEventListener(NATIVE_CALL_EVENT, handler as EventListener);
  return () => window.removeEventListener(NATIVE_CALL_EVENT, handler as EventListener);
}

export function endNativeAudioRuntimeCall(callSessionId: CallSessionId): void {
  endNativeCall(callSessionId);
}

export function toggleNativeAudioRuntimeMute(callSessionId: CallSessionId, muted: boolean): void {
  setNativeCallMuted(callSessionId, muted);
}

export function setNativeVideoRuntimeLocalVideoEnabled(callSessionId: CallSessionId, enabled: boolean): void {
  setNativeCallLocalVideoEnabled(callSessionId, enabled);
}

export function switchNativeVideoRuntimeCamera(callSessionId: CallSessionId): void {
  switchNativeCallCamera(callSessionId);
}

export function stopNativeIncomingRinging(callSessionId?: CallSessionId | string | null): void {
  stopIncomingNativeRinging(callSessionId);
}
